import React from 'react';
import type { Article, Maybe, Scalars } from '@seek/cmsu-cms-connect';
import {
  Badge,
  Box,
  Divider,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue,
} from 'braid-design-system';
import { articleCard, articleCardImage } from './styles.css';
import { useTranslations } from '@vocab/react';
import translations from '../../.vocab';
import {
  articleActionByType,
  resolveArticlePathFrom,
} from '../../shared/utils/helper';
import { useMelwaysLink } from '@seek/melways-react';
import type { Language } from '@seek/melways-sites';
import { formatAbsoluteDatePreferredFormat } from 'src/helpers/dateHelper';

type Props = {
  article: Article;
  language: Language;
  hideCategory?: boolean;
  isLastCard?: boolean;
};

export const ArticleCard = ({
  article,
  language,
  hideCategory = false,
  isLastCard = false,
}: Props) => {
  const urlResolver = useMelwaysLink();
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();
  const isTabletAbove = responsiveValue({
    mobile: false,
    desktop: true,
    tablet: true,
    wide: true,
  });

  const articleActionTranslated = t(articleActionByType(article.articleType));
  const path = resolveArticlePathFrom(article);
  const articleLink = urlResolver({
    language,
    path,
  });

  const articleCardFooterText = getArticleCardFooter({
    publishDate: article.publishDate,
    timeToRead: article.timeToRead,
    articleActionTranslated,
  });

  return (
    <Stack space="gutter">
      <Box borderRadius="large" className={articleCard}>
        <Box data-testid="ArticleFeatureImageTestId">
          <img
            src={article.featureImage[0]?.url}
            className={articleCardImage}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="spaceBetween"
          flexDirection={'column'}
          padding={{ mobile: 'gutter', tablet: 'none' }}
        >
          <Box paddingBottom="large">
            <Stack space="gutter">
              {!hideCategory && article.category && (
                <Box data-testid="ArticleCategoryTestId">
                  <Badge tone="neutral">{article.category.name}</Badge>
                </Box>
              )}
              <Heading level="3">
                <TextLink href={articleLink}>{article.title}</TextLink>
              </Heading>
            </Stack>
          </Box>
          <Text tone="secondary" size="small">
            {articleCardFooterText}
          </Text>
        </Box>
      </Box>

      {isTabletAbove && !isLastCard && <Divider weight="regular" />}
    </Stack>
  );
};

export const getArticleCardFooter = ({
  publishDate,
  timeToRead,
  articleActionTranslated,
}: {
  publishDate: Scalars['Date'];
  timeToRead?: Maybe<number> | undefined;
  articleActionTranslated?: string;
}) => {
  const articleTimeAction = timeToRead
    ? `${timeToRead} ${articleActionTranslated} · `
    : '';
  return articleTimeAction + formatAbsoluteDatePreferredFormat(publishDate);
};
